/**
 * useProperties — TanStack Query hook over
 * `GET /organizations/:slug/properties`.
 *
 * The shape mirrors `useUsersList`'s closed-status union (loading /
 * ready / forbidden / error) — that pattern was load-bearing for the
 * SCRUM-108 forbidden-vs-error branching, and the property list has
 * the same need: a 403 means "you lost `property.read` since
 * `/auth/me` cached" and the page renders a permission-denied panel
 * instead of the generic Retry affordance.
 *
 * The hook:
 *   - reads the org slug via `useCurrentOrg()` (RLS-scoped per
 *     CLAUDE.md non-negotiable §1)
 *   - keeps the BE response shape opaque (no decoding) but projects
 *     each row through `projectPropertyDto` so consumers see the
 *     bundle-aligned `PropertyView` (see SCRUM-167 plan)
 *   - applies client-side filters (q / status / type) AFTER fetching
 *     — Phase 1 stays under the bundle's "client-side filter when
 *     total < 500" threshold
 *   - uses `placeholderData: keepPreviousData` so the page doesn't
 *     flash into a loading state on every filter change (same UX
 *     reason as UsersListPage)
 *
 * The Phase-1 endpoint takes only `?includeArchived=` server-side;
 * `q` / `status` / `type` are FE-applied. When the BE adds server-side
 * search/filter, the hook signature stays the same — only the
 * `queryFn`'s param-building grows.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import { useCurrentOrg } from '../../../shared/hooks/useCurrentOrg.js';
import { projectPropertyDto } from './dto-mapper.js';
import type { PropertiesListInput, PropertyView } from './types.js';
import { propertyQueryKeys } from './queryKeys.js';

/**
 * Internal sentinel for the 403 branch — same idiom as
 * `use-users-list.ts`. The queryFn returns this symbol on 403 so we
 * can distinguish forbidden from a generic error in the hook's
 * status union.
 */
const FORBIDDEN = Symbol('properties-list:forbidden');

export type UsePropertiesResult =
  | {
      readonly status: 'loading';
      readonly data?: undefined;
      readonly total?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'ready';
      /** All rows after client-side filters apply. */
      readonly data: readonly PropertyView[];
      /** Pre-filter total from the BE envelope. */
      readonly total: number;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'forbidden';
      readonly data?: undefined;
      readonly total?: undefined;
      readonly refetch: () => void;
    }
  | {
      readonly status: 'error';
      readonly data?: undefined;
      readonly total?: undefined;
      readonly refetch: () => void;
    };

/**
 * Apply the bundle's client-side filters to the projected views. Kept
 * exported so the page can verify filter behaviour against fixture
 * data in tests without booting TanStack.
 */
export function filterPropertyViews(
  views: readonly PropertyView[],
  input: Pick<PropertiesListInput, 'q' | 'status' | 'type'>,
): readonly PropertyView[] {
  const q = input.q?.trim().toLowerCase() ?? '';
  return views.filter((view) => {
    if (q.length > 0) {
      const haystack = `${view.name} ${view.address.city}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    if (input.status !== undefined && input.status !== 'all') {
      if (input.status === 'bookable' && view.status.av !== 'bookable') {
        return false;
      }
      if (
        input.status === 'non-bookable' &&
        view.status.av !== 'non-bookable'
      ) {
        return false;
      }
    }
    if (input.type !== undefined && input.type !== 'all') {
      if (view.type !== input.type) return false;
    }
    return true;
  });
}

export function useProperties(
  input: PropertiesListInput = {},
): UsePropertiesResult {
  const orgState = useCurrentOrg();
  const orgSlug = orgState?.membership.organizationSlug ?? null;

  const query = useQuery({
    queryKey: [
      ...propertyQueryKeys.list({}),
      orgSlug,
      input.includeArchived ?? false,
    ] as const,
    enabled: orgSlug !== null,
    retry: false,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await apiClient.GET(
        '/organizations/{slug}/properties',
        {
          params: {
            path: { slug: orgSlug as string },
            query: input.includeArchived === true
              ? { includeArchived: true }
              : {},
          },
        },
      );
      if (result.response.status === 403) {
        return FORBIDDEN;
      }
      if (!result.response.ok || result.data === undefined) {
        throw new Error(
          `GET /organizations/${orgSlug}/properties failed with status ${result.response.status}`,
        );
      }
      return result.data;
    },
  });

  const refetch = (): void => {
    void query.refetch();
  };

  if (orgSlug === null || query.isPending) {
    return { status: 'loading', refetch };
  }
  if (query.isError) {
    return { status: 'error', refetch };
  }
  if (query.data === FORBIDDEN) {
    return { status: 'forbidden', refetch };
  }

  const envelope = query.data;
  const projected = envelope.data.map(projectPropertyDto);
  const filtered = filterPropertyViews(projected, input);

  return {
    status: 'ready',
    data: filtered,
    total: envelope.total,
    refetch,
  };
}
